import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobileDevice, isLocalDevelopment } from '../../utils/config';
import './OtpStep.css';

interface OtpStepProps {
  cvrt: string;
  loading: boolean;
  error: string | null;
  onVerifyOtp: (cvrt: string, otp: string) => Promise<boolean>;
  onNext: () => void;
  onResendOtp?: () => Promise<boolean>;
}

const OtpStep: React.FC<OtpStepProps> = ({
  cvrt,
  loading,
  error,
  onVerifyOtp,
  onNext,
  onResendOtp,
}) => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [verified, setVerified] = useState(false);
  const [waitingForSms, setWaitingForSms] = useState(false);
  const [hasSubmittedOtp, setHasSubmittedOtp] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-submit when OTP is complete
  useEffect(() => {
    const otpString = otp.join('');
    if (otpString.length === 6 && !loading && !verified && !hasSubmittedOtp && !error) {
      if (isLocalDevelopment()) {
        console.log('[OtpStep] OTP complete, auto-submitting...');
      }
      // Call submit directly without using handleSubmit to avoid circular dependency
      (async () => {
        setHasSubmittedOtp(true);
        setVerified(false);
        try {
          const success = await onVerifyOtp(cvrt, otpString);
          if (success) {
            setVerified(true);
            setTimeout(() => {
              onNext();
            }, 2000);
          } else {
            // If not successful, don't reset hasSubmittedOtp to prevent re-submission
            if (isLocalDevelopment()) {
              console.log('[OtpStep] OTP verification failed, preventing re-submission');
            }
          }
        } catch (err) {
          console.error('Error verifying OTP:', err);
          // Keep hasSubmittedOtp as true to prevent infinite loop
        }
      })();
    }
  }, [otp, loading, verified, hasSubmittedOtp, error, cvrt, onVerifyOtp, onNext]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // WebOTP API - Auto-read OTP from SMS on mobile devices
    const isMobile = isMobileDevice();
    if ('OTPCredential' in window && isMobile) {
      const abortController = new AbortController();

      const requestOtp = async () => {
        try {
          if (isLocalDevelopment()) {
            console.log('[OtpStep] Requesting OTP via WebOTP API on mobile device...');
          }
          setWaitingForSms(true);

          const credential = await (navigator as any).credentials.get({
            otp: { transport: ['sms'] },
            signal: abortController.signal
          });

          if (credential && credential.code) {
            if (isLocalDevelopment()) {
              console.log('[OtpStep] OTP received via WebOTP:', credential.code);
            }
            // Auto-fill the OTP
            const otpArray = credential.code.split('').slice(0, 6);
            setOtp(prev => {
              const newOtp = [...prev];
              otpArray.forEach((digit: string, index: number) => {
                if (index < 6) {
                  newOtp[index] = digit;
                }
              });
              return newOtp;
            });

            // Focus the last input or submit button
            if (inputRefs.current[5]) {
              inputRefs.current[5].focus();
            }

            setWaitingForSms(false);
          }
        } catch (err) {
          // User cancelled or other error - this is normal
          if (isLocalDevelopment()) {
            console.log('[OtpStep] WebOTP cancelled or not available:', err);
          }
          setWaitingForSms(false);
        }
      };

      requestOtp();

      // Cleanup - abort the request if component unmounts
      return () => {
        abortController.abort();
      };
    } else {
      if (isLocalDevelopment()) {
        if (!isMobile) {
          console.log('[OtpStep] WebOTP API disabled on desktop device');
        } else {
          console.log('[OtpStep] WebOTP API not available on this device/browser');
        }
      }
    }
  }, []);

  // Handle cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Clear success message after a delay
  useEffect(() => {
    if (resendSuccess) {
      const timer = setTimeout(() => {
        setResendSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [resendSuccess]);

  const handleInputChange = (index: number, value: string) => {
    // Reset submission flag when user changes the OTP
    if (hasSubmittedOtp) {
      setHasSubmittedOtp(false);
    }
    
    if (value.length > 1) {
      // Handle paste
      const pastedOtp = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedOtp.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      
      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + pastedOtp.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex]?.focus();
      }
    } else {
      // Handle single digit
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      // Reset submission flag when user edits the OTP
      if (hasSubmittedOtp) {
        setHasSubmittedOtp(false);
      }
      
      if (!otp[index] && index > 0) {
        // Move focus to previous input on backspace
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleClear = () => {
    setOtp(['', '', '', '', '', '']);
    setHasSubmittedOtp(false);
    // Focus first input after clear
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  const handleResend = async () => {
    if (!onResendOtp || resendLoading || resendCooldown > 0) return;

    setResendLoading(true);
    setResendSuccess(false);

    // Clear any previous OTP and errors
    setOtp(['', '', '', '', '', '']);
    setHasSubmittedOtp(false);

    try {
      const success = await onResendOtp();
      if (success) {
        setResendSuccess(true);
        setResendCooldown(30); // 30 second cooldown
        // Focus first input for new OTP
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }
    } catch (err) {
      console.error('Error resending OTP:', err);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    const otpString = otp.join('');
    if (otpString.length !== 6 || loading) {
      return;
    }

    // Set flag to prevent auto-submit from triggering
    setHasSubmittedOtp(true);
    // Reset verified state in case of retry
    setVerified(false);

    try {
      const success = await onVerifyOtp(cvrt, otpString);
      if (success) {
        setVerified(true);
        // Auto-advance to next step after successful verification
        setTimeout(() => {
          onNext();
        }, 2000);
      } else {
        // Keep hasSubmittedOtp as true to prevent auto-resubmission
        if (isLocalDevelopment()) {
          console.log('[OtpStep] Manual OTP verification failed');
        }
      }
      // If not successful, error will be displayed via the error prop
    } catch (err) {
      // Error handling is done in the parent component
      console.error('Error verifying OTP:', err);
      // Keep hasSubmittedOtp as true to prevent auto-resubmission
    }
  };

  if (verified && !error) {
    return (
      <div className="otp-step">
        <div className="card">
          <div className="card-header">
            <h2>{t('otp.title')}</h2>
          </div>
          <div className="card-body">
            <div className="success-container">
              <div className="alert alert-success">
                <div className="success-icon">✅</div>
                <p>Verification successful!</p>
                <p>Loading your card details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="otp-step">
      <div className="card otp-card">
        <div className="card-header otp-header">
          <h2>{t('otp.title')}</h2>
          <p>{t('otp.description')}</p>
        </div>
        <div className="card-body otp-body">
          <form onSubmit={handleSubmit} className="otp-form">
            {waitingForSms && (
              <div className="sms-waiting-indicator">
                <div className="info-box">
                  <span className="spinner-small"></span>
                  <span>Waiting for SMS... Your code will be filled automatically</span>
                </div>
              </div>
            )}

            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="otp-input"
                  disabled={loading}
                  placeholder=""
                />
              ))}
            </div>

            {otp.some(digit => digit !== '') && !loading && (
              <div className="clear-otp-container">
                <button
                  type="button"
                  className="btn-link clear-otp-btn"
                  onClick={handleClear}
                  title="Clear OTP"
                >
                  <i className="fa fa-times-circle"></i> Clear
                </button>
              </div>
            )}

            {!loading && !error && (
              <div className="auto-verify-hint">
                <i className="fa fa-info-circle"></i>
                <span>{t('otp.autoVerifyHint')}</span>
              </div>
            )}

            {error && (
              <div className="alert alert-error">
                <strong>{t('common.error')}</strong>
                <p>{error}</p>
              </div>
            )}

            {loading && (
              <div className="verification-status">
                <span className="btn-spinner"></span>
                <span>{t('otp.verifying')}</span>
              </div>
            )}

            {error && (
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={otp.join('').length !== 6}
                >
                  {t('otp.retry')}
                </button>
              </div>
            )}

            <div className="resend-container">
              {resendSuccess && (
                <div className="resend-success">
                  <span className="success-icon">✓</span>
                  {t('otp.resendSuccess')}
                </div>
              )}
              <button
                type="button"
                className="btn-link resend-link"
                onClick={handleResend}
                disabled={!onResendOtp || loading || resendLoading || resendCooldown > 0}
              >
                {resendLoading ? (
                  <>
                    <span className="btn-spinner-small"></span>
                    {t('otp.resending')}
                  </>
                ) : resendCooldown > 0 ? (
                  `${t('otp.resend')} (${resendCooldown}s)`
                ) : (
                  t('otp.resend')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OtpStep;